import { useState, useEffect, useCallback, useRef } from 'react';
import { Socket } from 'socket.io-client';
import { socketService } from '@/services/socketService';
import { queryChatbot, handleChatbotError } from '@/services/chatbotService';
import type { ChatMessage, ServerToClientEvents, ClientToServerEvents } from '@/types/chatbot';

interface UseChatbotOptions {
  groupId: string;
  memberId: string;
  useStreaming?: boolean;
  autoConnect?: boolean;
}

interface UseChatbotReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  isStreaming: boolean;
  isConnected: boolean;
  error: string | null;
  sendMessage: (question: string) => Promise<void>;
  clearMessages: () => void;
  reconnect: () => void;
}

/**
 * Custom React hook for chatbot integration with WebSocket streaming
 * 
 * @param options - Configuration options
 * @returns Chatbot state and methods
 * 
 * @example
 * ```tsx
 * const { messages, sendMessage, isLoading } = useChatbot({
 *   groupId: 'team-123',
 *   memberId: 'user-456',
 *   useStreaming: true
 * });
 * ```
 */
export function useChatbot({
  groupId,
  memberId,
  useStreaming = true,
  autoConnect = true,
}: UseChatbotOptions): UseChatbotReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const socketRef = useRef<Socket<ServerToClientEvents, ClientToServerEvents> | null>(null);
  const currentStreamingMessageRef = useRef<string>('');
  const isMountedRef = useRef(true);

  /**
   * Initialize Socket.IO connection and event listeners
   */
  useEffect(() => {
    isMountedRef.current = true;

    if (!useStreaming || !autoConnect) {
      return;
    }

    // Connect to WebSocket
    const socket = socketService.connect(memberId);
    socketRef.current = socket;

    // Update connection status
    setIsConnected(socket.connected);

    // Connection status listeners
    const handleConnect = () => {
      if (isMountedRef.current) {
        setIsConnected(true);
        setError(null);
      }
    };

    const handleDisconnect = () => {
      if (isMountedRef.current) {
        setIsConnected(false);
      }
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    // Streaming event listeners
    const handleStreamStart = (data: { question: string; memberId: string }) => {
      if (data.memberId !== memberId || !isMountedRef.current) return;

      setIsStreaming(true);
      setError(null);
      currentStreamingMessageRef.current = '';
      
      // Add placeholder message for assistant
      setMessages((prev) => [
        ...prev,
        {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          streaming: true,
        },
      ]);
    };

    const handleStreamChunk = (data: { chunk: string; question: string; memberId: string }) => {
      if (data.memberId !== memberId || !isMountedRef.current) return;
      
      // Accumulate chunks
      currentStreamingMessageRef.current += data.chunk;
      
      // Update the last message with accumulated content
      setMessages((prev) => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage && lastMessage.streaming) {
          lastMessage.content = currentStreamingMessageRef.current;
        }
        return newMessages;
      });
    };

    const handleStreamComplete = (data: { response: string; question: string; memberId: string }) => {
      if (data.memberId !== memberId || !isMountedRef.current) return;

      setIsStreaming(false);
      setIsLoading(false);
      
      // Finalize the message
      setMessages((prev) => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage && lastMessage.streaming) {
          lastMessage.content = data.response;
          lastMessage.streaming = false;
        }
        return newMessages;
      });
      
      currentStreamingMessageRef.current = '';
    };

    const handleStreamError = (data: { error: string; question: string }) => {
      if (!isMountedRef.current) return;

      setError(handleChatbotError(new Error(data.error)));
      setIsStreaming(false);
      setIsLoading(false);
      
      // Remove incomplete streaming message
      setMessages((prev) => prev.filter((msg) => !msg.streaming));
    };

    const handleChatbotResponse = (data: { response: string; question: string }) => {
      if (!isMountedRef.current) return;

      // Non-streaming response
      setMessages((prev) => [
        ...prev,
        {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          content: data.response,
          timestamp: new Date(),
        },
      ]);
      setIsLoading(false);
    };

    // Register event listeners
    socket.on('stream_start', handleStreamStart);
    socket.on('stream_chunk', handleStreamChunk);
    socket.on('stream_complete', handleStreamComplete);
    socket.on('stream_error', handleStreamError);
    socket.on('chatbot_response', handleChatbotResponse);

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      
      // Remove event listeners
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('stream_start', handleStreamStart);
      socket.off('stream_chunk', handleStreamChunk);
      socket.off('stream_complete', handleStreamComplete);
      socket.off('stream_error', handleStreamError);
      socket.off('chatbot_response', handleChatbotResponse);
      
      // Disconnect socket
      socketService.disconnect();
    };
  }, [memberId, useStreaming, autoConnect]);

  /**
   * Send a message to the chatbot
   */
  const sendMessage = useCallback(
    async (question: string) => {
      if (!question.trim()) {
        setError('Please enter a question');
        return;
      }

      if (!groupId || !memberId) {
        setError('Missing group ID or member ID');
        return;
      }

      setError(null);
      setIsLoading(true);

      // Add user message immediately
      const userMessage: ChatMessage = {
        id: `msg-${Date.now()}`,
        role: 'user',
        content: question,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      try {
        // IMPORTANT: Wait briefly to ensure room join is confirmed by backend
        // Backend needs to acknowledge room membership before streaming
        if (useStreaming && socketRef.current?.connected) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
        
        // Always use REST API to trigger the query (backend pattern)
        // The backend will stream response back via WebSocket if stream: true
        const response = await queryChatbot(groupId, memberId, question, useStreaming);
        
        // If not using streaming or backend didn't enable streaming
        if (!useStreaming || !response.streaming) {
          // Add assistant response directly
          setMessages((prev) => [
            ...prev,
            {
              id: `msg-${Date.now()}`,
              role: 'assistant',
              content: response.response || 'No response received',
              timestamp: new Date(),
            },
          ]);
          setIsLoading(false);
        }
        // If streaming is enabled, response will come via WebSocket events
        // Keep isLoading true until stream completes
      } catch (err: any) {
        const errorMessage = handleChatbotError(err);
        setError(errorMessage);
        setIsLoading(false);
        setIsStreaming(false);
        
        // Remove the user message if request failed
        setMessages((prev) => prev.filter((msg) => msg.id !== userMessage.id));
      }
    },
    [groupId, memberId, useStreaming]
  );

  /**
   * Clear all messages
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    currentStreamingMessageRef.current = '';
  }, []);

  /**
   * Manually reconnect to WebSocket
   */
  const reconnect = useCallback(() => {
    if (useStreaming && memberId) {
      const socket = socketService.connect(memberId);
      socketRef.current = socket;
      setIsConnected(socket.connected);
    }
  }, [memberId, useStreaming]);

  return {
    messages,
    isLoading,
    isStreaming,
    isConnected,
    error,
    sendMessage,
    clearMessages,
    reconnect,
  };
}
